// src/features/streams/stores/streamGridState.ts

import { create } from 'zustand';
import {
  DEFAULT_STREAM_SORT_DIRECTION,
  DEFAULT_STREAM_SORT_KEY,
  STREAM_SORT_KEYS,
} from '@/lib/constants/stream';
import { GridSortStorage } from '@/shared/utils/gridSortStorage';
import type { SearchMode } from '@/lib/types/Media';
import type { SortDirection } from '@/lib/types/common';
import type { StreamSortKey } from '@/lib/types/Stream';

const SORT_STORAGE_FEATURE = 'streams';

function loadPersistedSort(): Pick<StreamGridState, 'sortKey' | 'sortDirection'> {
  const stored = GridSortStorage.load(SORT_STORAGE_FEATURE, STREAM_SORT_KEYS, {
    key: DEFAULT_STREAM_SORT_KEY,
    direction: DEFAULT_STREAM_SORT_DIRECTION,
  });
  return { sortKey: stored.key, sortDirection: stored.direction };
}

interface StreamGridState {
  searchText: string;
  searchMode: SearchMode;
  sortKey: StreamSortKey;
  sortDirection: SortDirection;
  scrollTop: number;
  awaitingBackNavPaint: boolean;
  orderedIdsSnapshot: number[];
  orderedIdsSignature: string | null;
  orderedIdsQueryKey: string | null;
}

interface StreamGridActions {
  setSearchText: (text: string) => void;
  setSearchMode: (mode: SearchMode) => void;
  setSort: (key: StreamSortKey) => void;
  setScrollTop: (top: number) => void;
  setOrderedIdsSnapshot: (signature: string, queryKey: string, ids: number[]) => void;
  markBackNavStart: () => void;
  completeBackNavPaint: () => void;
  reset: () => void;
}

const initialState: StreamGridState = {
  searchText: '',
  searchMode: 'fuzzy',
  ...loadPersistedSort(),
  scrollTop: 0,
  awaitingBackNavPaint: false,
  orderedIdsSnapshot: [],
  orderedIdsSignature: null,
  orderedIdsQueryKey: null,
};

export const useStreamGridState = create<StreamGridState & StreamGridActions>()((set) => ({
  ...initialState,

  setSearchText: (text) => set({ searchText: text }),

  setSearchMode: (mode) => set({ searchMode: mode }),

  setSort: (key) =>
    set((state) => {
      const next: Pick<StreamGridState, 'sortKey' | 'sortDirection'> =
        state.sortKey === key
          ? { sortKey: key, sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc' }
          : { sortKey: key, sortDirection: 'desc' };
      GridSortStorage.save(SORT_STORAGE_FEATURE, next.sortKey, next.sortDirection);
      return next;
    }),

  setScrollTop: (top) =>
    set((state) => {
      if (Math.abs(state.scrollTop - top) < 1) return state;
      return { scrollTop: top };
    }),

  setOrderedIdsSnapshot: (signature, queryKey, ids) =>
    set((state) => {
      if (
        state.orderedIdsSignature === signature &&
        state.orderedIdsQueryKey === queryKey &&
        state.orderedIdsSnapshot === ids
      ) {
        return state;
      }

      return {
        orderedIdsSignature: signature,
        orderedIdsQueryKey: queryKey,
        orderedIdsSnapshot: ids,
      };
    }),

  markBackNavStart: () => set({ awaitingBackNavPaint: true }),

  completeBackNavPaint: () => set({ awaitingBackNavPaint: false }),

  // Sort survives reset: it's a persisted browser preference, not session state.
  reset: () => set({ ...initialState, ...loadPersistedSort() }),
}));
